import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';
import { toast } from '@/hooks/useToast';
import { PaymentOptionsForm } from './PaymentOptionsForm';
import { useProposalPaymentStep } from '../hooks/useProposalPaymentStep';
import { paymentOptionsErrorKey } from '../hooks/usePaymentOptions';

interface PaymentOptionsCardProps {
    itineraryId: string;
}

/**
 * I metodi di pagamento della proposta nel checkout del builder.
 *
 * Si scelgono all'invio, ma devono restare modificabili dopo: se il cliente chiede di
 * pagare con bonifico, il TA non deve reinviare la proposta per abilitarlo. Stesso form
 * e stessa RPC dello step di send-quote.
 */
export const PaymentOptionsCard = ({ itineraryId }: PaymentOptionsCardProps) => {
    const { t } = useI18n();
    const payment = useProposalPaymentStep(itineraryId);
    const [dirty, setDirty] = useState(false);

    // Cambio di itinerario senza smontare il builder: niente salvataggi pendenti.
    useEffect(() => setDirty(false), [itineraryId]);

    const handleSave = async () => {
        if (!payment.validate()) return;
        try {
            await payment.save();
            setDirty(false);
            toast({ variant: 'success', title: t('paymentOptions.builder.saved') });
        } catch (error) {
            toast({ title: t(paymentOptionsErrorKey(error)), variant: 'destructive' });
        }
    };

    return (
        <Card className="w-full">
            <CardContent className="p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-900">{t('paymentOptions.builder.title')}</span>
                    {dirty && (
                        <Button type="button" size="sm" onClick={handleSave} disabled={payment.isSaving}>
                            {payment.isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                            {t('common.save')}
                        </Button>
                    )}
                </div>
                {payment.form ? (
                    <PaymentOptionsForm
                        value={payment.form}
                        onChange={(next) => {
                            payment.onChange(next);
                            setDirty(true);
                        }}
                        errors={payment.errors}
                        stripeActive={payment.stripeActive}
                        defaults={payment.defaults}
                    />
                ) : (
                    <div className="flex justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
